import type { TFunction } from "i18next";
import {
  buildHotelSettingsNavigationGroups,
  buildNavigationGroups,
  buildNavigationGroupsAdmin,
  buildNavigationGroupsSuperAdmin,
  type NavGroup,
  type NavItem,
} from "@/components/layout/constants/navigation";
import {
  filterNavigationGroupsByVisibility,
  type NavigationVisibilityContext,
} from "@/components/layout/hooks/useGetNavigationMenu";
import type { UserProfile } from "@/services/authService";
import type { GlobalSearchItem } from "@/services/global-search-service";
import {
  isHotelScopedSubscriptionRole,
  isPlatformAdminRole,
} from "@/utils/accessControl";

interface GlobalSearchNavigationParams {
  capabilities: NavigationVisibilityContext["capabilities"];
  t: TFunction;
  userProfile: UserProfile | null;
}

interface GlobalSearchNavigationItem extends GlobalSearchItem {
  aliases: string[];
  href: string;
  searchText: string;
}

type HotelSidebarRole = "HOTEL_ADMIN" | "FRONT_DESK" | "STAFF" | undefined;

const SEARCH_NAVIGATION_ALIASES_BY_HREF: Partial<Record<string, string[]>> = {
  "/general-info": [
    "general info",
    "hotel info",
    "hotel brand settings",
    "hotel_brand_settings",
  ],
  "/settings": ["hotel brand settings", "hotel_brand_settings"],
  "/mobile-settings": [
    "app customization",
    "mobile settings",
    "hotel brand settings",
    "hotel_brand_settings",
  ],
  "/mobile-settings/dashboard": [
    "dashboard settings",
    "mobile settings",
    "hotel brand settings",
    "hotel_brand_settings",
  ],
  "/custom-pages": ["pages", "custom pages", "guest pages"],
  "/hotels": ["hotel brands", "hotel_brands", "hotels", "hotel brands list"],
  "/amenities": ["amenities"],
  "/checkin-settings": [
    "check in check out",
    "checkin checkout",
    "checkin_checkout",
  ],
  "/chats": ["guest communication", "guest_communication"],
  "/services": ["hotel services", "hotel_services"],
  "/staff": ["staff operations", "staff_operations"],
  "/internal-chats": ["internal chat", "internal_chat"],
  "/requests": ["requests system", "requests_system"],
  "/requests-settings": [
    "requests system",
    "requests_system",
    "requests settings",
  ],
  "/messaging-settings": ["whatsapp integration", "whatsapp_integration"],
  "/billing-settings": ["billing", "guest payments", "payments", "stripe"],
  "/billing-transactions": [
    "billing",
    "guest payments",
    "guest transactions",
    "payments",
  ],
  "/subscription-payments": [
    "billing",
    "subscription payments",
    "subscription transactions",
  ],
  "/guesteria-partners-revenue": [
    "partners revenue",
    "guesteria partners revenue",
  ],
  "/partner-integration-settings": [
    "partners settings",
    "partner integration settings",
  ],
  "/modules": ["system modules", "modules"],
  "/sso-settings": ["sso", "single sign-on"],
};

function normalizeSearchValue(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function getHotelSidebarRole(
  userProfile: UserProfile | null,
): HotelSidebarRole {
  if (
    userProfile?.role === "HOTEL_ADMIN" ||
    userProfile?.role === "FRONT_DESK" ||
    userProfile?.role === "STAFF"
  ) {
    return userProfile.role;
  }

  return undefined;
}

function buildNavigationGroupsForSearch(
  params: GlobalSearchNavigationParams,
): NavGroup[] {
  const { capabilities, t, userProfile } = params;
  const isHotelScopedRole = isHotelScopedSubscriptionRole(userProfile);
  const context: NavigationVisibilityContext = {
    capabilities,
    isHotelScopedRole,
    role: userProfile?.role,
    userProfile,
  };

  if (isPlatformAdminRole(userProfile)) {
    return filterNavigationGroupsByVisibility(
      buildNavigationGroupsSuperAdmin(t),
      context,
    );
  }

  if (userProfile?.role === "TENANT_ADMIN") {
    return filterNavigationGroupsByVisibility(
      buildNavigationGroupsAdmin(t),
      context,
    );
  }

  const defaultNavigation = filterNavigationGroupsByVisibility(
    buildNavigationGroups(t),
    context,
  );
  const settingsNavigation = filterNavigationGroupsByVisibility(
    buildHotelSettingsNavigationGroups(t, getHotelSidebarRole(userProfile)),
    context,
  );

  return [...defaultNavigation, ...settingsNavigation];
}

function buildNavigationAliases(
  item: NavItem,
  subtitle: string | undefined,
  parentLabels: string[],
): string[] {
  const hrefParts = item.href
    .split("/")
    .filter(Boolean)
    .map((part) => part.replace(/-/g, " "));
  const manualAliases = SEARCH_NAVIGATION_ALIASES_BY_HREF[item.href] ?? [];
  const rawAliases = [
    item.title,
    subtitle ?? "",
    item.href,
    ...parentLabels,
    ...hrefParts,
    ...manualAliases,
  ];

  return Array.from(
    new Set(
      rawAliases.map(normalizeSearchValue).filter((value) => value.length > 0),
    ),
  );
}

function flattenNavigationItems(
  items: NavItem[],
  groupTitle: string | undefined,
  parentLabels: string[] = [],
): GlobalSearchNavigationItem[] {
  return items.flatMap((item) => {
    const subtitleParts = [groupTitle, ...parentLabels].filter(
      (value): value is string => Boolean(value),
    );
    const subtitle =
      subtitleParts.length > 0 ? subtitleParts.join(" / ") : undefined;
    const aliases = buildNavigationAliases(item, subtitle, parentLabels);
    const currentItem: GlobalSearchNavigationItem = {
      aliases,
      href: item.href,
      id: item.href,
      label: item.title,
      searchText: aliases.join(" "),
      subtitle,
      type: "module",
    };
    const nextParentLabels = [...parentLabels, item.title];
    const nestedItems =
      item.items !== undefined
        ? flattenNavigationItems(item.items, groupTitle, nextParentLabels)
        : [];

    if (item.isGroupOnly) {
      return nestedItems;
    }

    return [currentItem, ...nestedItems];
  });
}

export function getGlobalSearchNavigationItems(
  params: GlobalSearchNavigationParams,
): GlobalSearchNavigationItem[] {
  const groups = buildNavigationGroupsForSearch(params);
  const flattenedItems = groups.flatMap((group) =>
    flattenNavigationItems(group.items, group.title),
  );
  const navigationItemByHref = new Map<string, GlobalSearchNavigationItem>();

  flattenedItems.forEach((item) => {
    navigationItemByHref.set(item.href, item);
  });

  return Array.from(navigationItemByHref.values());
}

export function filterGlobalSearchNavigationItems(
  items: GlobalSearchNavigationItem[],
  query: string,
): GlobalSearchNavigationItem[] {
  const normalizedQuery = normalizeSearchValue(query);

  if (normalizedQuery.length < 2) {
    return [];
  }

  return items.filter((item) => item.searchText.includes(normalizedQuery));
}

export function resolveGlobalSearchModuleHref(
  item: GlobalSearchItem,
  navigationItems: GlobalSearchNavigationItem[],
): string | null {
  if (item.id.startsWith("/")) {
    return item.id;
  }

  const normalizedCandidates = Array.from(
    new Set(
      [item.id, item.label, item.subtitle ?? ""]
        .map(normalizeSearchValue)
        .filter((value) => value.length > 0),
    ),
  );

  for (const navigationItem of navigationItems) {
    const hasMatch = normalizedCandidates.some((candidate) =>
      navigationItem.aliases.some(
        (alias) =>
          alias === candidate ||
          alias.includes(candidate) ||
          candidate.includes(alias),
      ),
    );

    if (hasMatch) {
      return navigationItem.href;
    }
  }

  return null;
}
